/**
 * Wire types — Brief 5 B5 Anonimização (Sprint Frontend Brief 5 · Tela 3).
 *
 * Hand-written (decisão do projeto) espelhando o serializer REAL do Engine
 * (apps/engine/src/modules/lgpd/anonymization.service.ts → SerializedAnonymization
 *  + dto/anonymization.dto.ts). Triangulado campo-a-campo (R15) — NÃO derivado
 * do mockup (anonimizacao.html), que usa um contrato amigável diferente.
 *
 * Gate: `lgpd-anonymization-execute` (tierMin Professional · requiresCapability
 * lgpd-requests-management — catalog.ts).
 *
 * 🔴 DRIFT vs `centro-direitos/types.ts` (R15 — bug latente, NÃO reusado aqui):
 *   - level: o tipo antigo usa `partial|full|delete_cascade`; o enum Prisma REAL é
 *     `total|contextual|statistical` (o backend rejeita o resto via Zod).
 *   - status: o antigo usa `executing`; o enum REAL é `in_progress` e inclui `held`.
 *   - datas: o antigo usa `createdAt/executionStartedAt/executionCompletedAt`; o
 *     serializer REAL retorna `requestedAt/startedAt/completedAt`.
 *   Por isso a B5 define o contrato correto aqui em vez de reusar o drifted.
 *   Cleanup do tipo antigo (consumido por centro-direitos) = Item futuro.
 *
 * Honest-defers visíveis (balde B): ImpactPreview real (dry-run), co-signer
 * Regulated, audit-config (opIdLabel/retention/notify). `entityId` é coletado
 * pelo FE (obrigatório no BE); `tenant-all` é bloqueado. `documentReferenceId`
 * é write-only: guardado em `metadata` no BE e NÃO retornado pelo serializer.
 */

// ─── Enums canônicos (Prisma — apps/engine/prisma/schema.prisma) ──────────────

export type AnonymizationLevel = 'total' | 'contextual' | 'statistical';
export type AnonymizationStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'held';

// ─── Serializer real (POST create/execute/complete · GET list) ────────────────

export interface AnonymizationWireItem {
  id: string;
  tenantId: string;
  lgpdRequestId: string | null;
  entityType: string;
  entityId: string;
  level: AnonymizationLevel;
  status: AnonymizationStatus;
  executedByUserId: string | null;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  failureReason: string | null;
  operationResult: Record<string, unknown>;
}

// ─── Requests (DTO real — Zod) ────────────────────────────────────────────────

export interface CreateAnonymizationRequest {
  entityType: string;
  entityId: string;
  level: AnonymizationLevel;
  lgpdRequestId?: string;
  documentReferenceId?: string;
}

export interface CompleteAnonymizationRequest {
  success: boolean;
  failureReason?: string;
}

// ─── Preview / dry-run (Fatia 1 motor ADR-110 — 10/Jun/2026) ──────────────────
//
// Espelha o serializer REAL de AnonymizationPreviewService (R15):
// POST /v1/lgpd/anonymizations/preview → AnonymizationPreviewPlan DIRETO
// (sem envelope {data}). v1 cobre entityType='person' (400 honesto p/ demais).

export type AnonymizationFieldAction =
  | 'placeholder'
  | 'null_hash'
  | 'mask'
  | 'hash'
  | 'redact'
  | 'clear'
  | 'keep'
  | 'marker';

export type PreviewSnapshotMode = 'hashed' | 'plaintext';

export interface PreviewFieldRule {
  field: string;
  action: AnonymizationFieldAction;
  detail: string;
}

export interface PreviewBlocker {
  code: 'legal_hold_active' | 'already_anonymized' | 'anonymization_in_progress';
  detail: string;
  legalHoldId?: string;
  anonymizationId?: string;
}

export interface PreviewEntityPlan {
  entityType: 'person' | 'identity' | 'structured_collection';
  entityId: string;
  label: string;
  fields: PreviewFieldRule[];
}

export interface PreviewDeferredItem {
  entityType: string;
  count: number;
  coveredBy: string;
}

export interface AnonymizationPreviewPlan {
  entityType: 'person';
  entityId: string;
  level: AnonymizationLevel;
  fieldMapVersion: number;
  snapshotMode: PreviewSnapshotMode;
  executable: boolean;
  blockers: PreviewBlocker[];
  entities: PreviewEntityPlan[];
  deferred: PreviewDeferredItem[];
}

export interface PreviewAnonymizationRequest {
  entityType: string;
  entityId: string;
  level: AnonymizationLevel;
}

/** Labels PT das ações do mapa canônico (surfa no painel dry-run). */
export const FIELD_ACTION_META: Record<
  AnonymizationFieldAction,
  { label: string; tone: 'destructive' | 'masked' | 'kept' }
> = {
  placeholder: { label: 'Substituído por placeholder', tone: 'destructive' },
  null_hash: { label: 'Anulado (hash forense preservado)', tone: 'destructive' },
  mask: { label: 'Mascarado (original no snapshot forense)', tone: 'masked' },
  hash: { label: 'Hasheado (anon:sha256)', tone: 'destructive' },
  redact: { label: 'Redatado', tone: 'destructive' },
  clear: { label: 'Removido', tone: 'destructive' },
  keep: { label: 'Mantido', tone: 'kept' },
  marker: { label: 'Recebe marcador de anonimização', tone: 'kept' },
};

export const PREVIEW_ENTITY_LABEL: Record<PreviewEntityPlan['entityType'], string> = {
  person: 'Pessoa',
  identity: 'Identidade de canal',
  structured_collection: 'Coleta estruturada',
};

export const DEFERRED_ENTITY_LABEL: Record<string, string> = {
  conversation: 'Conversas',
  bond_participant: 'Participações em bonds',
};

// ─── Escopos (entityType) — espelha o mockup; `tenant-all` BLOQUEADO ──────────
//
// O mockup oferece escopos amigáveis; o BE quer o `entityType` concreto. Cada
// escopo é um `entityType` string real. `tenant-all` é anti-pattern (use B1
// LGPDRequest formal) e por isso NÃO é oferecido como opção.

export const ENTITY_TYPE_OPTIONS: readonly {
  value: string;
  label: string;
  hint: string;
}[] = [
  {
    value: 'person',
    label: 'Pessoa específica',
    hint: 'Todos os dados de UMA pessoa — a cascata atinge vínculos, conversas e documentos associados.',
  },
  {
    value: 'bond',
    label: 'Bond / vínculo',
    hint: 'Um vínculo (contrato/relação) preservando as pessoas — útil para bond encerrado sem direito ao esquecimento.',
  },
  {
    value: 'conversation',
    label: 'Conversa',
    hint: 'Conversa pontual — geralmente conteúdo sensível registrado indevidamente.',
  },
  {
    value: 'document',
    label: 'Document',
    hint: 'Redação de um documento mantendo as referências que apontam para ele.',
  },
  {
    value: 'subject',
    label: 'Subject (registro externo)',
    hint: 'Subject vindo do SuperGer ERP — coerência cross-system.',
  },
];

// ─── Mapas UI (enum REAL → label + descrição pt-BR, ADR-110) ──────────────────

export const ANONYMIZATION_LEVEL_META: Record<AnonymizationLevel, { label: string; hint: string }> =
  {
    // Hints alinhados ao mapa canônico v1 ratificado (10/Jun/2026) — registros e
    // referências sempre preservados (nunca hard-delete); contextual = mask-on-write.
    // Copy na língua do DPO (sem "PII/rows/FKs" — jargão dev).
    total: {
      label: 'Total',
      hint: 'Dados pessoais removidos do registro (substituídos por marcador + hash forense); registros e referências preservados para auditoria. Eliminação real — snapshot forense sem dado em claro.',
    },
    contextual: {
      label: 'Contextual',
      hint: 'Dados pessoais mascarados no registro; o original fica APENAS no snapshot forense (visível só ao DPO). Padrão recomendado — preserva operação e estatísticas.',
    },
    statistical: {
      label: 'Estatístico',
      hint: 'Mantém apenas referências e agregados; todo o restante é removido (snapshot forense).',
    },
  };

export const ANONYMIZATION_STATUS_META: Record<
  AnonymizationStatus,
  { label: string; tone: 'neutral' | 'progress' | 'done' | 'error' | 'held' }
> = {
  pending: { label: 'Pendente', tone: 'neutral' },
  in_progress: { label: 'Em execução', tone: 'progress' },
  completed: { label: 'Concluída', tone: 'done' },
  failed: { label: 'Falhou', tone: 'error' },
  held: { label: 'Bloqueada (LegalHold)', tone: 'held' },
};

// ─── Helpers de lifecycle (status real → transições permitidas) ───────────────

/** Só `pending` pode ser executada (BE rejeita o resto). */
export function canExecute(status: AnonymizationStatus): boolean {
  return status === 'pending';
}

/**
 * Só `in_progress` pode ser concluída — e `person` NUNCA (Fatia 2 motor
 * ADR-110: o executor síncrono completa sozinho após o efeito real; o BE
 * recusa complete manual de person com success=true).
 */
export function canComplete(status: AnonymizationStatus, entityType?: string): boolean {
  return status === 'in_progress' && entityType !== 'person';
}
